import { useState, useEffect } from 'react';
import type { SaRole } from '@/mocks/saRolesPermissions';
import { ENGINE_LABELS, ALL_ENGINES } from '@/mocks/saRolesPermissions';
import PermissionMatrix from './PermissionMatrix';

interface Props {
  role: SaRole | null;
  onClose: () => void;
  onEdit: (role: SaRole) => void;
}

type TabKey = 'permissions' | 'history' | 'users';

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'permissions', label: 'Matriz de Permisos', icon: 'ri-key-2-line' },
  { key: 'history', label: 'Historial de Cambios', icon: 'ri-history-line' },
  { key: 'users', label: 'Usuarios', icon: 'ri-group-line' },
];

export default function RoleDetailModal({ role, onClose, onEdit }: Props) {
  const [activeTab, setActiveTab] = useState<TabKey>('permissions');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (role) {
      setActiveTab('permissions');
      const timer = setTimeout(() => setVisible(true), 10);
      return () => clearTimeout(timer);
    }
    setVisible(false);
  }, [role]);

  if (!role) return null;

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose(), 200);
  };

  const engineCount = role.permissions.length;
  const enginesWithAccess = role.permissions.filter((p) => p.actions.length > 0).length;
  const totalPerms = role.permissions.reduce((s, p) => s + p.actions.length, 0);

  return (
    <div className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={handleClose}></div>

      {/* Modal */}
      <div className={`relative bg-background-50 rounded-3xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl transition-transform duration-200 ${visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}>
        {/* Header */}
        <div className="px-6 py-5 border-b border-background-200 shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: role.color ? `${role.color}18` : 'var(--background-200)', color: role.color || '#6b7280' }}
              >
                <i className={`${role.icon} text-lg`}></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-foreground-950 font-bold text-base">{role.name}</h3>
                <p className="text-foreground-400 text-xs mt-0.5 line-clamp-2">{role.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold ${role.active ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-foreground-100 text-foreground-500 border border-foreground-200'}`}>
                {role.active ? 'Activo' : 'Inactivo'}
              </span>
              <button
                onClick={() => { handleClose(); onEdit(role); }}
                className="w-9 h-9 rounded-xl bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                title="Editar rol"
              >
                <i className="ri-edit-line text-foreground-600 text-sm"></i>
              </button>
              <button
                onClick={handleClose}
                className="w-9 h-9 rounded-xl bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
              >
                <i className="ri-close-line text-foreground-600 text-sm"></i>
              </button>
            </div>
          </div>

          {/* Quick stats */}
          <div className="flex items-center gap-4 mt-4">
            <div className="flex items-center gap-1.5">
              <span className="text-foreground-400 text-[10px]">Nivel:</span>
              <span className="text-foreground-800 text-xs font-semibold">{role.hierarchyLevel}</span>
            </div>
            <div className="w-px h-3 bg-background-300"></div>
            <div className="flex items-center gap-1.5">
              <span className="text-foreground-400 text-[10px]">Engines:</span>
              <span className="text-foreground-800 text-xs font-semibold">{enginesWithAccess}/{ALL_ENGINES.length}</span>
            </div>
            <div className="w-px h-3 bg-background-300"></div>
            <div className="flex items-center gap-1.5">
              <span className="text-foreground-400 text-[10px]">Permisos:</span>
              <span className="text-foreground-800 text-xs font-semibold">{totalPerms}</span>
            </div>
            <div className="w-px h-3 bg-background-300"></div>
            <div className="flex items-center gap-1.5">
              <span className="text-foreground-400 text-[10px]">Usuarios:</span>
              <span className="text-foreground-800 text-xs font-semibold">{role.userCount.toLocaleString()}</span>
            </div>
            <div className="w-px h-3 bg-background-300"></div>
            <div className="flex items-center gap-1.5">
              <span className="text-foreground-400 text-[10px]">Tipo:</span>
              <span className={`text-xs font-semibold ${role.isSystem ? 'text-amber-600' : 'text-violet-600'}`}>
                {role.isSystem ? 'Sistema' : 'Personalizado'}
              </span>
            </div>
            {role.criticalPermissions.length > 0 && (
              <>
                <div className="w-px h-3 bg-background-300"></div>
                <div className="flex items-center gap-1.5">
                  <i className="ri-alert-line text-rose-500 text-[10px]"></i>
                  <span className="text-rose-600 text-xs font-semibold">{role.criticalPermissions.length} accesos críticos</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-3 border-b border-background-200 shrink-0">
          <div className="flex items-center gap-1">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-t-xl text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-background-50 text-foreground-950 border border-background-200 border-b-background-50 -mb-px'
                    : 'text-foreground-400 hover:text-foreground-600'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'permissions' && (
            <PermissionMatrix role={role} readOnly />
          )}

          {activeTab === 'history' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-history-line text-foreground-400 text-sm"></i>
                <span className="text-foreground-500 text-xs font-medium">Registro de cambios del rol</span>
                <span className="px-1.5 py-0.5 rounded-full bg-background-200 text-foreground-500 text-[9px] font-bold">{role.changeLog.length} cambios</span>
              </div>
              {role.changeLog.length === 0 ? (
                <div className="py-8 text-center">
                  <div className="w-12 h-12 mx-auto rounded-xl bg-background-100 flex items-center justify-center mb-2">
                    <i className="ri-check-double-line text-foreground-300 text-lg"></i>
                  </div>
                  <p className="text-foreground-500 text-xs">Sin cambios registrados</p>
                  <p className="text-foreground-400 text-[10px] mt-0.5">El rol no ha sido modificado desde su creación.</p>
                </div>
              ) : (
                role.changeLog.map((log, idx) => (
                  <div key={log.id} className="flex items-start gap-3 p-3.5 rounded-xl bg-background-100/70 border border-background-200">
                    <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${idx === 0 ? 'bg-primary-500' : 'bg-background-300'}`}></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-foreground-700 text-xs font-semibold">{log.field}</span>
                        <span className="text-foreground-400 text-[10px]">{log.changedAt}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-600 text-[10px] font-medium line-through">{log.oldValue}</span>
                        <i className="ri-arrow-right-line text-foreground-300 text-xs"></i>
                        <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-600 text-[10px] font-medium">{log.newValue}</span>
                      </div>
                      <p className="text-foreground-400 text-[10px] mt-1">por {log.changedBy}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <i className="ri-group-line text-foreground-400 text-sm"></i>
                <span className="text-foreground-500 text-xs font-medium">Usuarios con este rol asignado</span>
                <span className="px-1.5 py-0.5 rounded-full bg-background-200 text-foreground-500 text-[9px] font-bold">{role.userCount.toLocaleString()}</span>
              </div>

              {/* Summary card */}
              <div className="p-4 rounded-2xl bg-background-100/70 border border-background-200">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-foreground-950 font-bold text-2xl">{role.userCount.toLocaleString()}</div>
                    <div className="text-foreground-400 text-[10px] mt-0.5">usuarios totales</div>
                  </div>
                  <div className="w-px h-10 bg-background-300"></div>
                  <div className="text-center">
                    <div className="text-emerald-600 font-bold text-lg">{Math.round(role.userCount * 0.82).toLocaleString()}</div>
                    <div className="text-foreground-400 text-[10px] mt-0.5">activos</div>
                  </div>
                  <div className="w-px h-10 bg-background-300"></div>
                  <div className="text-center">
                    <div className="text-amber-600 font-bold text-lg">{Math.round(role.userCount * 0.11).toLocaleString()}</div>
                    <div className="text-foreground-400 text-[10px] mt-0.5">inactivos</div>
                  </div>
                  <div className="w-px h-10 bg-background-300"></div>
                  <div className="text-center">
                    <div className="text-rose-600 font-bold text-lg">{Math.round(role.userCount * 0.07).toLocaleString()}</div>
                    <div className="text-foreground-400 text-[10px] mt-0.5">suspendidos</div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 px-3 py-3 rounded-xl bg-accent-50 border border-accent-200">
                <i className="ri-information-line text-accent-500 text-sm"></i>
                <p className="text-accent-700 text-xs">
                  La visualización detallada de usuarios por rol estará disponible al conectar con el Identity Engine y Supabase.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-background-200 shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-2 text-foreground-400 text-[10px]">
            <span>ID: {role.slug}</span>
            <span>·</span>
            <span>Creado: {role.createdAt}</span>
          </div>
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-semibold cursor-pointer hover:bg-background-200 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}